import React from 'react';
import { ColorPicker, parseColor } from '@ark-ui/react/color-picker';
import { Portal } from '../../../components/Portal';
import clsx from 'clsx';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface ColorPickerInputProps extends BaseInputProps<string> {
  /** Výchozí barva při prázdné hodnotě (hex). */
  fallbackColor?: string;
  /**
   * Povolit alfa (průhlednost). Výchozí `true` — hodnota je `#rrggbbaa` a
   * zobrazí se alfa slider. Při `false` se alfa slider skryje a hodnota je
   * neprůhledný `#rrggbb`.
   */
  allowAlpha?: boolean;
  className?: string;
}

const POPOVER_POSITIONING = { placement: 'bottom-start', gutter: 4 } as const;

/**
 * Atomický výběr barvy postavený na Ark UI `ColorPicker`.
 *
 * RHF drží hodnotu jako hex řetězec (`#rrggbbaa`) — serializovatelný a přímo
 * znovu-parsovatelný (`parseColor`). `ids.trigger` mapuje `id` z `<FormField />`
 * (klik na popisek fokusuje trigger), `ids.label` jej pojmenuje pro AT.
 */
export const ColorPickerInput: React.FC<ColorPickerInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  required,
  describedBy,
  labelId,
  testAttributes,
  fallbackColor = '#000000',
  allowAlpha = true,
  className,
}) => {
  const ids = React.useMemo(
    () => ({ trigger: id, label: labelId }),
    [id, labelId],
  );

  return (
  <ColorPicker.Root
    name={name}
    ids={ids}
    value={parseColor(value && value.length > 0 ? value : fallbackColor)}
    disabled={disabled}
    readOnly={readOnly}
    invalid={hasError}
    required={required}
    onValueChange={(details) =>
      onChange(details.value.toString(allowAlpha ? 'hexa' : 'hex'))
    }
    onValueChangeEnd={() => onBlur()}
    positioning={POPOVER_POSITIONING}
    lazyMount
    unmountOnExit
    className={clsx(styles.colorRoot, className)}
  >
    <ColorPicker.Control className={styles.colorControl}>
      <ColorPicker.ChannelInput channel="hex" className={styles.colorInput} />
      <ColorPicker.Trigger
        ref={ref}
        className={styles.colorTrigger}
        aria-describedby={describedBy}
      >
        <ColorPicker.ValueSwatch className={styles.colorSwatch} />
      </ColorPicker.Trigger>
    </ColorPicker.Control>
    {/* Skrytý form input nese hodnotu pro nativní submit — axe vyžaduje
        i u něj přístupné jméno, proto aria-labelledby na popisek FormFieldu. */}
    <ColorPicker.HiddenInput aria-labelledby={labelId} {...testAttributes} />
    <Portal>
      <ColorPicker.Positioner>
        <ColorPicker.Content
          className={styles.colorContent}
          data-testid={`${testAttributes['data-testid']}-content`}
        >
          <ColorPicker.Area className={styles.colorArea}>
            <ColorPicker.AreaBackground className={styles.colorAreaBackground} />
            <ColorPicker.AreaThumb className={styles.colorAreaThumb} />
          </ColorPicker.Area>
          <div className={styles.colorSliders}>
            <ColorPicker.ChannelSlider
              channel="hue"
              className={styles.colorChannelSlider}
            >
              <ColorPicker.ChannelSliderTrack
                className={styles.colorChannelTrack}
              />
              <ColorPicker.ChannelSliderThumb
                className={styles.colorChannelThumb}
              />
            </ColorPicker.ChannelSlider>
            {allowAlpha && (
              <ColorPicker.ChannelSlider
                channel="alpha"
                className={styles.colorChannelSlider}
              >
                <ColorPicker.ChannelSliderTrack
                  className={styles.colorChannelTrack}
                />
                <ColorPicker.ChannelSliderThumb
                  className={styles.colorChannelThumb}
                />
              </ColorPicker.ChannelSlider>
            )}
          </div>
        </ColorPicker.Content>
      </ColorPicker.Positioner>
    </Portal>
  </ColorPicker.Root>
  );
};

ColorPickerInput.displayName = 'ColorPickerInput';
